import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Users, UserPlus, Calendar, Clock } from 'lucide-react';
import apiClient from '../../utils/apiClient';

interface User {
  id: number;
  username: string;
  displayName: string;
  roleLevel: number;
  icon: string;
  iconColor: string;
  thumbnailUrl?: string;
  isOwner: boolean;
  isAdmin: boolean;
}

const PinLoginFormEnhanced: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [pin, setPin] = useState('');
  const [showPinPad, setShowPinPad] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAllStaff, setShowAllStaff] = useState(false);
  const [displayMode, setDisplayMode] = useState<'shift' | 'all'>('shift');

  // 現在の日時を表示
  const [currentDateTime, setCurrentDateTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    fetchActiveUsers();
  }, [showAllStaff]);

  const fetchActiveUsers = async () => {
    try {
      const response = await apiClient.get('/auth/active-users', {
        params: { showAll: showAllStaff }
      });
      if (response.data.success) {
        console.log('取得したユーザーデータ:', response.data.users);
        setUsers(response.data.users);
        setDisplayMode(response.data.mode);
      }
    } catch (error) {
      console.error('ユーザーリスト取得エラー:', error);
    }
  };

  const handleUserSelect = (user: User) => {
    setSelectedUser(user);
    setPin('');
    setError('');
    setShowPinPad(true);
  };

  const handlePinInput = (digit: string) => {
    if (pin.length < 8) {  // 最大8桁に拡張
      const newPin = pin + digit;
      setPin(newPin);

      // 開発者用クイックアクセス（999999で即ログイン）
      if (newPin === '999999' && selectedUser?.roleLevel === 3) {
        handlePinLogin(newPin);
      }
    }
  };

  const handlePinDelete = () => {
    setPin(pin.slice(0, -1));
  };

  const handlePinClear = () => {
    setPin('');
  };

  const handlePinLogin = async (pinCode: string = pin) => {
    if (!selectedUser) return;

    // 権限別の最小桁数チェック
    const roleLevel = selectedUser.roleLevel;
    let minLength = 3;
    let requiresSymbol = false;

    if (roleLevel === 2) {
      // オーナー: 最小4桁・記号任意
      minLength = 4;
      requiresSymbol = false;
    } else if (roleLevel === 0 || roleLevel === 1) {
      // スタッフ: 最小3桁・記号任意
      minLength = 3;
      requiresSymbol = false;
    }
    // 開発者（roleLevel === 3）は制限なし

    if (pinCode.length < minLength) {
      setError(`PINコードは${minLength}桁以上必要です`);
      return;
    }

    if (requiresSymbol && roleLevel !== 3) {
      const symbols = ['♠', '♥', '♦', '♣', '〇', '△', '□', '☆'];
      const hasSymbol = symbols.some(s => pinCode.includes(s));
      if (!hasSymbol) {
        setError('オーナー権限では記号を1つ以上含める必要があります');
        return;
      }
    }

    setLoading(true);
    setError('');

    try {
      const response = await apiClient.post('/auth/pin-login', {
        userId: selectedUser.id,
        pin: pinCode
      });

      if (response.data.success) {
        const { user, token } = response.data;
        // トークンをローカルストレージに保存
        localStorage.setItem('waton_token', token);
        // ページをリロードして認証状態を更新
        window.location.href = '/';
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'ログインに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (date: Date) => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    };
    return date.toLocaleDateString('ja-JP', options);
  };

  const PinPad = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-8 w-full max-w-md">
        <div className="text-center mb-6">
          {selectedUser?.thumbnailUrl ? (
            <img
              src={selectedUser.thumbnailUrl}
              alt={selectedUser.displayName}
              className="w-24 h-24 rounded-full mx-auto mb-4 object-cover border-4"
              style={{ borderColor: selectedUser.iconColor }}
            />
          ) : (
            <div className="text-6xl mb-4">{selectedUser?.icon}</div>
          )}
          <h3 className="text-2xl font-bold">{selectedUser?.displayName}</h3>
          <p className="text-gray-600 mt-2">PINコードを入力してください</p>
        </div>

        <div className="mb-6">
          <div className="flex justify-center items-center space-x-2">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className={`w-12 h-12 border-2 rounded-lg flex items-center justify-center ${
                  i < pin.length ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                }`}
              >
                {i < pin.length && <span className="text-white text-2xl">●</span>}
              </div>
            ))}
          </div>
          {error && (
            <p className="text-red-500 text-center mt-2">{error}</p>
          )}
        </div>

        <div className="grid grid-cols-5 gap-3 mb-4">
          {[
            [1, 2, 3, '♠', '♥'],
            [4, 5, 6, '♦', '♣'],
            [7, 8, 9, '〇', '△'],
            [0, '←', 'C', '□', '☆']
          ].flat().map((digit, index) => (
            <button
              key={`${digit}-${index}`}
              onClick={() => {
                if (digit === '←') handlePinDelete();
                else if (digit === 'C') handlePinClear();
                else handlePinInput(digit.toString());
              }}
              className={`py-4 text-2xl font-bold rounded-lg transition-colors ${
                digit === '←' || digit === 'C'
                  ? 'bg-gray-300 hover:bg-gray-400 text-gray-700'
                  : typeof digit === 'number'
                  ? 'bg-white border-2 border-blue-300 hover:bg-blue-50 text-blue-600'
                  : 'bg-gradient-to-br from-purple-100 to-pink-100 border-2 border-purple-300 hover:from-purple-200 hover:to-pink-200 text-purple-600'
              }`}
              disabled={loading}
            >
              {digit}
            </button>
          ))}
        </div>

        <div className="flex space-x-3">
          <button
            onClick={() => {
              setShowPinPad(false);
              setSelectedUser(null);
              setPin('');
              setError('');
            }}
            className="flex-1 py-3 bg-gray-300 rounded-lg font-medium hover:bg-gray-400"
            disabled={loading}
          >
            キャンセル
          </button>
          <button
            onClick={() => handlePinLogin()}
            className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
            disabled={loading || pin.length < 3}
          >
            {loading ? 'ログイン中...' : 'ログイン'}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-100 p-4">
      <div className="max-w-7xl mx-auto">
        {/* ヘッダー部分 */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">和とんPOSシステム</h1>
              <p className="text-gray-600 mt-1">スタッフを選択してログインしてください</p>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-2 text-lg font-semibold text-gray-700">
                <Calendar className="h-5 w-5" />
                <Clock className="h-5 w-5" />
                {formatDateTime(currentDateTime)}
              </div>
              <div className="mt-2">
                {displayMode === 'shift' ? (
                  <span className="text-sm text-blue-600 font-medium">
                    📅 本日のシフトスタッフ表示中
                  </span>
                ) : (
                  <span className="text-sm text-green-600 font-medium">
                    👥 全スタッフ表示中
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 表示切替ボタン */}
        <div className="flex justify-center mb-6">
          <button
            onClick={() => setShowAllStaff(!showAllStaff)}
            className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
              showAllStaff
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {showAllStaff ? (
              <>
                <Users className="h-5 w-5" />
                全スタッフを表示中
              </>
            ) : (
              <>
                <Calendar className="h-5 w-5" />
                本日のシフトのみ表示中
              </>
            )}
          </button>
        </div>

        {/* スタッフ一覧 */}
        {users.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-md">
            <UserPlus className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-lg">
              {displayMode === 'shift'
                ? '本日のシフトに登録されているスタッフがいません'
                : '登録されているスタッフがいません'}
            </p>
            {displayMode === 'shift' && (
              <button
                onClick={() => setShowAllStaff(true)}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                全スタッフを表示
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {users.map((user) => (
              <button
                key={user.id}
                onClick={() => handleUserSelect(user)}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all transform hover:scale-105 relative"
                style={{
                  borderTop: `4px solid ${user.iconColor}`
                }}
              >
                {user.isOwner && (
                  <div className="absolute top-2 right-2">
                    <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">
                      オーナー
                    </span>
                  </div>
                )}
                {user.thumbnailUrl ? (
                  <img
                    src={user.thumbnailUrl}
                    alt={user.displayName}
                    className="w-20 h-20 rounded-full mx-auto mb-3 object-cover border-4"
                    style={{ borderColor: user.iconColor }}
                  />
                ) : (
                  <div className="text-6xl mb-3">{user.icon}</div>
                )}
                <div className="font-bold text-lg">{user.displayName}</div>
                {user.isAdmin && !user.isOwner && (
                  <span className="inline-block mt-2 px-2 py-1 bg-red-100 text-red-600 text-xs rounded-full">
                    管理者
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {/* 開発者用ヒント（本番環境では削除） */}
        {process.env.NODE_ENV === 'development' && (
          <div className="text-center mt-8 text-xs text-gray-500">
            開発者: admin / 999999 | オーナー: katsu / 1234
          </div>
        )}
      </div>

      {showPinPad && <PinPad />}
    </div>
  );
};

export default PinLoginFormEnhanced;
